import {
  ApiError,
  type BusinessHours,
  type Mailbox,
  MAX_PHONE_AUDIO_BYTES,
  queryKeys,
  SUPPORT_IVR_DIGITS,
  SUPPORT_PHONE_VOICES,
  type SupportCallTarget,
  type SupportCannedResponse,
  type SupportEmailIntakeMode,
  type SupportIvrAction,
  type SupportIvrMode,
  type SupportIvrOption,
  type SupportReceptionistFaq,
  type SupportSettings,
  type SupportWebChatAvailabilityMode,
  type TicketCloseNotes,
  type UpdateSupportSettingsInput,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  Bot,
  Hash,
  ListChecks,
  Mail,
  MessagesSquare,
  Pencil,
  Phone,
  Plus,
  Square,
  Trash2,
  Upload,
  Volume2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { useSoftphone } from './softphone/softphone-provider.js'

interface StaffOption {
  id: string
  name: string
  isActive: boolean
}

const DAYS: { key: keyof NonNullable<BusinessHours>; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
]

const ALWAYS_OPEN_HOURS: NonNullable<BusinessHours> = {
  mon: { open: '09:00', close: '17:00' },
  tue: { open: '09:00', close: '17:00' },
  wed: { open: '09:00', close: '17:00' },
  thu: { open: '09:00', close: '17:00' },
  fri: { open: '09:00', close: '17:00' },
  sat: null,
  sun: null,
}

/**
 * How inbound calls and texts are handled: business hours, who a call rings
 * (in order), the voicemail greeting, and the after-hours text auto-reply.
 * Webhook URLs live on the Integrations → SMS card instead, alongside the
 * Twilio credentials they're paired with — this tab is purely "how the shop
 * wants contact handled," not a credential.
 */
export function SupportSettingsTab() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.supportSettings(),
    queryFn: () => api.get<SupportSettings>('/settings/support'),
  })

  return (
    <div className="space-y-5">
      <BusinessHoursCard data={data} isLoading={isLoading} />
      <PhoneVoiceCard data={data} isLoading={isLoading} />
      <PhoneMenuCard data={data} isLoading={isLoading} />
      <CallRoutingCard />
      <VoicemailCard data={data} isLoading={isLoading} />
      <TextWordingCard data={data} isLoading={isLoading} />
      <AiReceptionistCard data={data} isLoading={isLoading} />
      <WebChatCard data={data} isLoading={isLoading} />
      <EmailIntakeCard data={data} isLoading={isLoading} />
      <CloseNotesCard />
      <CannedResponsesCard />
    </div>
  )
}

function useSaveSupportSettings() {
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (patch: UpdateSupportSettingsInput) => api.patch('/settings/support', patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportSettings() })
      toast.success('Saved')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })
}

function BusinessHoursCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [alwaysOpen, setAlwaysOpen] = useState(true)
  const [hours, setHours] = useState<NonNullable<BusinessHours>>(ALWAYS_OPEN_HOURS)
  const [timezone, setTimezone] = useState('America/New_York')

  useEffect(() => {
    if (!data) return
    setAlwaysOpen(data.businessHours === null)
    setHours(data.businessHours ?? ALWAYS_OPEN_HOURS)
    setTimezone(data.timezone)
  }, [data])

  const setDay = (
    key: keyof NonNullable<BusinessHours>,
    value: { open: string; close: string } | null,
  ) => setHours((h) => ({ ...h, [key]: value }))

  const onSave = () => save.mutate({ timezone, businessHours: alwaysOpen ? null : hours })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Business hours</CardTitle>
        <CardDescription>
          Calls ring your team during these hours; outside them, a call goes straight to voicemail
          and — if set below — a new text gets an automatic reply.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Timezone" hint="An IANA name, e.g. America/New_York.">
          {(props) => (
            <Input
              {...props}
              value={timezone}
              disabled={isLoading}
              onChange={(e) => setTimezone(e.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Always open (no hours restriction)</span>
          <Switch checked={alwaysOpen} disabled={isLoading} onCheckedChange={setAlwaysOpen} />
        </div>

        {!alwaysOpen ? (
          <div className="space-y-2">
            {DAYS.map(({ key, label }) => {
              const day = hours[key]
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-sm">{label}</span>
                  <Switch
                    checked={day !== null}
                    onCheckedChange={(checked) =>
                      setDay(key, checked ? { open: '09:00', close: '17:00' } : null)
                    }
                  />
                  {day ? (
                    <>
                      <Input
                        type="time"
                        value={day.open}
                        onChange={(e) => setDay(key, { ...day, open: e.target.value })}
                        className="w-28"
                      />
                      <span className="text-foreground-subtle">–</span>
                      <Input
                        type="time"
                        value={day.close}
                        onChange={(e) => setDay(key, { ...day, close: e.target.value })}
                        className="w-28"
                      />
                    </>
                  ) : (
                    <span className="text-xs text-foreground-subtle">Closed</span>
                  )}
                </div>
              )
            })}
          </div>
        ) : null}

        <Button variant="primary" onClick={onSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * How often ticket texts carry "Mevaser Tech:" and "Reply STOP to opt out."
 * (t1233). Off = every text, as before; on = first text on a ticket, then again
 * after the chosen number of days.
 */
function TextWordingCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [firstOnly, setFirstOnly] = useState(false)
  const [days, setDays] = useState('7')

  useEffect(() => {
    if (!data) return
    setFirstOnly(data.smsWordingMode === 'first_and_periodic')
    setDays(String(data.smsWordingIntervalDays))
  }, [data])

  const daysNum = Number(days)
  const daysInvalid = !Number.isInteger(daysNum) || daysNum < 1 || daysNum > 90

  const onSave = () =>
    save.mutate({
      smsWordingMode: firstOnly ? 'first_and_periodic' : 'every_text',
      smsWordingIntervalDays: daysInvalid ? undefined : daysNum,
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Text message wording</CardTitle>
        <CardDescription>
          Texts sent from a ticket start with “Mevaser Tech:” and end with “&gt;Reply STOP to opt
          out.”
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Only add both to the first text on a ticket</span>
          <Switch checked={firstOnly} disabled={isLoading} onCheckedChange={setFirstOnly} />
        </div>
        {firstOnly ? (
          <Field
            label="Add them again after (days)"
            hint="Later texts on the same ticket go out as you typed them until this many days have passed since the last text that had them."
            error={daysInvalid ? 'A whole number between 1 and 90' : undefined}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                max={90}
                value={days}
                invalid={daysInvalid}
                onChange={(e) => setDays(e.target.value)}
              />
            )}
          </Field>
        ) : null}
        <p className="text-xs text-foreground-muted">
          The after-hours auto-reply always includes both. Twilio advises the brand name on every
          message, so check with your carrier before turning this on.
        </p>
        <Button variant="primary" onClick={onSave} disabled={save.isPending || daysInvalid}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

function VoicemailCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [greeting, setGreeting] = useState('')
  const [autoReply, setAutoReply] = useState('')
  const [autoReplyOn, setAutoReplyOn] = useState(false)
  const [dialTimeoutSeconds, setDialTimeoutSeconds] = useState('20')

  useEffect(() => {
    if (!data) return
    setGreeting(data.voicemailGreeting)
    setAutoReply(data.afterHoursSmsAutoReply ?? '')
    setAutoReplyOn(data.afterHoursSmsAutoReply !== null)
    setDialTimeoutSeconds(String(data.dialTimeoutSeconds))
  }, [data])

  const timeoutNum = Number(dialTimeoutSeconds)
  const timeoutInvalid = !Number.isInteger(timeoutNum) || timeoutNum < 5 || timeoutNum > 60

  const onSave = () =>
    save.mutate({
      voicemailGreeting: greeting.trim() || undefined,
      afterHoursSmsAutoReply: autoReplyOn ? autoReply.trim() || null : null,
      dialTimeoutSeconds: timeoutInvalid ? undefined : timeoutNum,
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Voicemail &amp; after-hours texts</CardTitle>
        <CardDescription>
          What a caller hears when nobody answers, and whether a text outside business hours gets an
          automatic reply.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label="How long to ring each person"
          hint="Seconds before moving to the next person on the list."
          error={timeoutInvalid ? 'A whole number between 5 and 60' : undefined}
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={5}
              max={60}
              value={dialTimeoutSeconds}
              disabled={isLoading}
              invalid={timeoutInvalid}
              onChange={(e) => setDialTimeoutSeconds(e.target.value)}
            />
          )}
        </Field>

        <Field label="Voicemail greeting">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={greeting}
              disabled={isLoading}
              onChange={(e) => setGreeting(e.target.value)}
            />
          )}
        </Field>

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Auto-reply to texts outside business hours</span>
          <Switch checked={autoReplyOn} disabled={isLoading} onCheckedChange={setAutoReplyOn} />
        </div>
        {autoReplyOn ? (
          <Field label="Auto-reply text">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={autoReply}
                onChange={(e) => setAutoReply(e.target.value)}
              />
            )}
          </Field>
        ) : null}
        {autoReplyOn ? (
          <p className="text-xs text-foreground-muted">
            Sent as “Mevaser Tech: … &gt;Reply STOP to opt out.” Both are added automatically.
          </p>
        ) : null}

        <Button variant="primary" onClick={onSave} disabled={save.isPending || timeoutInvalid}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * The AI receptionist (t0889): answers once the ring list is exhausted (or
 * the call arrives after hours) instead of going straight to voicemail. The
 * toggle and greeting live here on `supportSettings`, same as the voicemail
 * greeting above; the FAQ list it draws answers from is its own table
 * (`supportReceptionistFaqs`), managed in its own manager below, mirroring
 * `CannedResponsesCard`.
 */
function AiReceptionistCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [enabled, setEnabled] = useState(false)
  const [greeting, setGreeting] = useState('')
  const [answerTexts, setAnswerTexts] = useState(false)

  useEffect(() => {
    if (!data) return
    setEnabled(data.aiReceptionistEnabled)
    setGreeting(data.aiReceptionistGreeting)
    setAnswerTexts(data.smsAutoAnswerEnabled)
  }, [data])

  const { data: aiStatus } = useQuery({
    queryKey: queryKeys.ai.status(),
    queryFn: () => api.get<{ text: boolean; image: boolean }>('/ai/status'),
    staleTime: 60_000,
  })

  const onSave = () =>
    save.mutate({
      aiReceptionistEnabled: enabled,
      aiReceptionistGreeting: greeting,
      smsAutoAnswerEnabled: answerTexts,
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="size-4" aria-hidden="true" /> AI receptionist
        </CardTitle>
        <CardDescription>
          When staff don't answer — including outside business hours — an AI assistant answers
          instead of going straight to voicemail: it greets the caller, answers from the FAQ list
          below, and hands off to voicemail with what it learned if it can't help.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {aiStatus && !aiStatus.text ? (
          <Alert tone="info">
            No AI provider is configured yet under Settings → Integrations — turning this on will
            have no effect until one is.
          </Alert>
        ) : null}

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Answer unanswered calls</span>
          <Switch checked={enabled} disabled={isLoading} onCheckedChange={setEnabled} />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Also answer customer texts from the list below</span>
          <Switch checked={answerTexts} disabled={isLoading} onCheckedChange={setAnswerTexts} />
        </div>
        {answerTexts ? (
          <p className="text-xs text-foreground-muted">
            When a text clearly matches a question below, the answer is texted back automatically
            (with the usual brand and opt-out wording). Anything else is left for your team. At most
            3 automatic answers go out per ticket.
          </p>
        ) : null}

        <Field label="Greeting" hint="What the caller hears when the AI picks up.">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={greeting}
              disabled={isLoading}
              onChange={(e) => setGreeting(e.target.value)}
            />
          )}
        </Field>

        <Button variant="primary" onClick={onSave} disabled={save.isPending || !greeting.trim()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
      <ReceptionistFaqManager />
    </Card>
  )
}

/**
 * The storefront chat widget (t0948). Its own card, separate from the phone
 * receptionist above, because availability here is not just "AI on or off":
 * `webChatAvailability` decides whether the widget currently offers a human
 * agent at all, independent of whether the AI phase is reachable (that still
 * depends on an Anthropic key under Integrations, same as the receptionist).
 */
function WebChatCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [enabled, setEnabled] = useState(false)
  const [availability, setAvailability] = useState<SupportWebChatAvailabilityMode>('auto')
  const [greeting, setGreeting] = useState('')

  useEffect(() => {
    if (!data) return
    setEnabled(data.webChatEnabled)
    setAvailability(data.webChatAvailability)
    setGreeting(data.webChatGreeting)
  }, [data])

  const { data: aiStatus } = useQuery({
    queryKey: queryKeys.ai.status(),
    queryFn: () => api.get<{ text: boolean; image: boolean }>('/ai/status'),
    staleTime: 60_000,
  })

  const onSave = () =>
    save.mutate({
      webChatEnabled: enabled,
      webChatAvailability: availability,
      webChatGreeting: greeting,
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessagesSquare className="size-4" aria-hidden="true" /> Storefront chat
        </CardTitle>
        <CardDescription>
          Lets a shopper chat with an AI assistant on the website, request a human agent, and — if
          none is available — leave a message that shows up here as a support ticket.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {aiStatus && !aiStatus.text ? (
          <Alert tone="info">
            No AI provider is configured yet under Settings → Integrations — the widget will skip
            straight to "chat with an agent" until one is.
          </Alert>
        ) : null}

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Show the chat widget on the website</span>
          <Switch checked={enabled} disabled={isLoading} onCheckedChange={setEnabled} />
        </div>

        <Field
          label="Agent availability"
          hint="Controls whether the widget offers a human agent, or only AI chat and leave-a-message."
        >
          {(props) => (
            <Select
              value={availability}
              onValueChange={(v) => setAvailability(v as SupportWebChatAvailabilityMode)}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Follow business hours</SelectItem>
                <SelectItem value="online">Always online</SelectItem>
                <SelectItem value="offline">Always offline</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field label="Greeting" hint="What the shopper sees when they open the chat.">
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={greeting}
              disabled={isLoading}
              onChange={(e) => setGreeting(e.target.value)}
            />
          )}
        </Field>

        <Button variant="primary" onClick={onSave} disabled={save.isPending || !greeting.trim()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

function EmailIntakeCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [mode, setMode] = useState<SupportEmailIntakeMode>('off')
  const [mailboxId, setMailboxId] = useState('')
  const [autoReply, setAutoReply] = useState('')
  const [autoReplyOn, setAutoReplyOn] = useState(false)

  useEffect(() => {
    if (!data) return
    setMode(data.emailIntakeMode)
    setMailboxId(data.emailIntakeMailboxId ?? '')
    setAutoReply(data.afterHoursEmailAutoReply ?? '')
    setAutoReplyOn(data.afterHoursEmailAutoReply !== null)
  }, [data])

  const { data: mailboxes } = useQuery({
    queryKey: queryKeys.mail.mailboxes(),
    queryFn: () => api.get<Mailbox[]>('/mail/mailboxes'),
    staleTime: 60_000,
  })

  const { data: aiStatus } = useQuery({
    queryKey: queryKeys.ai.status(),
    queryFn: () => api.get<{ text: boolean; image: boolean }>('/ai/status'),
    staleTime: 60_000,
  })

  const onSave = () =>
    save.mutate({
      emailIntakeMode: mode,
      emailIntakeMailboxId: mailboxId || null,
      afterHoursEmailAutoReply: autoReplyOn ? autoReply.trim() || null : null,
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="size-4" aria-hidden="true" /> Support email
        </CardTitle>
        <CardDescription>
          Turns email sent to one of your mailboxes into support tickets. Staff can always create a
          ticket from any email by hand in the Mail tab, whatever is chosen here. A reply to an
          email that already has a ticket is added to that ticket instead of opening a new one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field
          label="Support mailbox"
          hint="Only email sent to this mailbox is considered. Add the address in the Mail tab first."
        >
          {(props) => (
            <Select
              value={mailboxId || 'none'}
              onValueChange={(v) => setMailboxId(v === 'none' ? '' : v)}
              disabled={isLoading}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None selected</SelectItem>
                {(mailboxes ?? []).map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field label="Create tickets">
          {(props) => (
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as SupportEmailIntakeMode)}
              disabled={isLoading}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Only when staff create them</SelectItem>
                <SelectItem value="all">For every email received</SelectItem>
                <SelectItem value="ai">Only for support questions (AI screens out spam)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        {mode !== 'off' && !mailboxId ? (
          <Alert tone="info">
            Choose a mailbox above, or nothing will be created automatically.
          </Alert>
        ) : null}
        {mode === 'ai' && aiStatus && !aiStatus.text ? (
          <Alert tone="info">
            No AI provider is configured yet under Settings → Integrations, so every email will
            become a ticket until one is.
          </Alert>
        ) : null}

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Auto-reply to new emails outside business hours</span>
          <Switch checked={autoReplyOn} disabled={isLoading} onCheckedChange={setAutoReplyOn} />
        </div>
        {autoReplyOn ? (
          <>
            <Field label="Auto-reply email">
              {(props) => (
                <Textarea
                  {...props}
                  rows={4}
                  value={autoReply}
                  onChange={(e) => setAutoReply(e.target.value)}
                />
              )}
            </Field>
            <p className="text-xs text-foreground-muted">
              Sent once, only when automatic intake opens a new ticket while the office is closed
              (see Business hours). Never sent to no-reply addresses or when staff create the ticket
              by hand. Your team can reply to the customer from the ticket.
            </p>
          </>
        ) : null}

        <Button variant="primary" onClick={onSave} disabled={save.isPending || isLoading}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  )
}

function ReceptionistFaqManager() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('settings:manage')
  const [editing, setEditing] = useState<SupportReceptionistFaq | 'new' | null>(null)

  const { data: faqs, isLoading } = useQuery({
    queryKey: queryKeys.supportReceptionistFaqs(),
    queryFn: () => api.get<SupportReceptionistFaq[]>('/settings/support/receptionist-faqs'),
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.supportReceptionistFaqs() })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/settings/support/receptionist-faqs/${id}`, { active }),
    onSuccess: invalidate,
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/settings/support/receptionist-faqs/${id}`),
    onSuccess: async () => {
      await invalidate()
      toast.success('Removed')
    },
    onError: (error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <div className="border-t border-border">
      <div className="flex items-center justify-between px-6 py-3">
        <div>
          <p className="text-sm font-medium">Common questions</p>
          <p className="text-xs text-foreground-muted">What the AI can answer callers from.</p>
        </div>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing('new')}>
            <Plus className="size-3.5" aria-hidden="true" /> Add
          </Button>
        ) : null}
      </div>
      {isLoading ? null : !faqs || faqs.length === 0 ? (
        <EmptyState
          icon={Bot}
          title="None yet"
          description="Add a question and answer the AI can use when a caller asks."
        />
      ) : (
        <ul className="divide-y divide-border">
          {faqs.map((faq) => (
            <li key={faq.id} className="flex items-start gap-2 px-6 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{faq.question}</p>
                <p className="truncate text-xs text-foreground-muted">{faq.answer}</p>
              </div>
              {!faq.active ? <Badge tone="neutral">Off</Badge> : null}
              {canWrite ? (
                <>
                  <Switch
                    checked={faq.active}
                    onCheckedChange={(active) => toggleActive.mutate({ id: faq.id, active })}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setEditing(faq)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${faq.question}`}
                    onClick={() => remove.mutate(faq.id)}
                  >
                    <Trash2 />
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <ReceptionistFaqDialog value={editing} onClose={() => setEditing(null)} />
    </div>
  )
}

function ReceptionistFaqDialog({
  value,
  onClose,
}: {
  value: SupportReceptionistFaq | 'new' | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')

  useEffect(() => {
    if (value === 'new') {
      setQuestion('')
      setAnswer('')
    } else if (value) {
      setQuestion(value.question)
      setAnswer(value.answer)
    }
  }, [value])

  const save = useMutation({
    mutationFn: () =>
      value === 'new'
        ? api.post('/settings/support/receptionist-faqs', { question, answer })
        : api.patch(`/settings/support/receptionist-faqs/${value?.id}`, { question, answer }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportReceptionistFaqs() })
      toast.success('Saved')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={value !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {value === 'new' ? 'Add common question' : 'Edit common question'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Question" hint="How a caller might ask it.">
            {(props) => (
              <Input {...props} value={question} onChange={(e) => setQuestion(e.target.value)} />
            )}
          </Field>
          <Field label="Answer">
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!question.trim() || !answer.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The keypad phone menu (t1100): what a caller hears first ("For our hours,
 * press 1…") and what each key does. The on/off switch, timing, opener and
 * closing line live on `supportSettings` like the voicemail greeting; the keys
 * are their own table (`supportIvrOptions`), managed below the settings the
 * same way the receptionist's FAQs are. The "For X, press N" lines are spoken
 * from the keys, so there is nothing to retype in the greeting when one changes.
 */
/**
 * t1405: the voice for everything the phone line says — the menu, the AI
 * receptionist, voicemail prompts. Listen plays the receptionist greeting in
 * the voice currently selected (saved or not) through the browser softphone,
 * so a voice can be judged before it goes live.
 */
function PhoneVoiceCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const toast = useToast()
  const { can } = useAuth()
  const { available, previewing, playIvrKeyPreview, stopIvrKeyPreview } = useSoftphone()
  const [voice, setVoice] = useState('default')

  useEffect(() => {
    if (data) setVoice(data.phoneVoice)
  }, [data])

  const listen = () => {
    if (previewing) {
      stopIvrKeyPreview()
      return
    }
    playIvrKeyPreview({ voice }, (message) => toast.error('Could not play', message)).catch(
      (error) => toast.error('Could not play', error instanceof Error ? error.message : undefined),
    )
  }
  const chosen = SUPPORT_PHONE_VOICES.find((v) => v.id === voice)

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="size-4" aria-hidden="true" /> Phone voice
        </CardTitle>
        <CardDescription>
          The voice callers hear for the phone menu, the AI receptionist and the voicemail prompt.
          The "Basic" voice is the robotic one; the others sound much more natural.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label="Voice" hint={chosen?.description}>
          {(props) => (
            <Select value={voice} onValueChange={setVoice}>
              <SelectTrigger {...props} disabled={isLoading}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORT_PHONE_VOICES.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.label} — {v.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          {available ? (
            <Button variant="secondary" onClick={listen} disabled={isLoading}>
              {previewing ? <Square /> : <Volume2 />}
              {previewing ? 'Stop' : 'Listen'}
            </Button>
          ) : null}
          <Button
            variant="primary"
            disabled={
              save.isPending || isLoading || !can('settings:manage') || voice === data?.phoneVoice
            }
            onClick={() => save.mutate({ phoneVoice: voice as SupportSettings['phoneVoice'] })}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function PhoneMenuCard({
  data,
  isLoading,
}: {
  data: SupportSettings | undefined
  isLoading: boolean
}) {
  const save = useSaveSupportSettings()
  const [enabled, setEnabled] = useState(false)
  const [mode, setMode] = useState<SupportIvrMode>('always')
  const [greeting, setGreeting] = useState('')
  const [closing, setClosing] = useState('')
  const [closingOn, setClosingOn] = useState(false)

  useEffect(() => {
    if (!data) return
    setEnabled(data.ivrEnabled)
    setMode(data.ivrMode)
    setGreeting(data.ivrGreeting)
    setClosing(data.ivrClosingMessage ?? '')
    setClosingOn(data.ivrClosingMessage !== null)
  }, [data])

  const closingInvalid = closingOn && !closing.trim()

  const onSave = () =>
    save.mutate({
      ivrEnabled: enabled,
      ivrMode: mode,
      ivrGreeting: greeting.trim() || undefined,
      ivrClosingMessage: closingOn ? closing.trim() || null : null,
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Hash className="size-4" aria-hidden="true" /> Phone menu
        </CardTitle>
        <CardDescription>
          A "press 1 for…" menu callers hear first. Add the keys below; the menu only plays once at
          least one key is on. Choosing customer service continues into call routing, the AI
          receptionist and voicemail as usual.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Play the phone menu</span>
          <Switch checked={enabled} disabled={isLoading} onCheckedChange={setEnabled} />
        </div>

        <Field label="When to play it">
          {(props) => (
            <Select value={mode} onValueChange={(v) => setMode(v as SupportIvrMode)}>
              <SelectTrigger {...props} disabled={isLoading}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="always">On every call</SelectItem>
                <SelectItem value="after_hours">Only outside business hours</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>

        <Field
          label="Greeting"
          hint={
            data?.ivrGreetingAudio
              ? 'Not used while a recording is set below — it is the fallback if you remove the recording.'
              : "Spoken first. The key choices are read out after it, so don't list them here."
          }
        >
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={greeting}
              disabled={isLoading}
              onChange={(e) => setGreeting(e.target.value)}
            />
          )}
        </Field>

        <RecordedGreeting audio={data?.ivrGreetingAudio ?? null} disabled={isLoading} />

        <div className="flex items-center justify-between rounded-md border border-border p-2.5">
          <span className="text-sm">Closing line after the choices</span>
          <Switch checked={closingOn} disabled={isLoading} onCheckedChange={setClosingOn} />
        </div>
        {closingOn ? (
          <Field
            label="Closing line"
            hint='For example "You can also text us directly at 555-0100." Change the number here whenever it changes.'
            error={closingInvalid ? 'Enter the closing line or turn it off' : undefined}
          >
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={closing}
                invalid={closingInvalid}
                onChange={(e) => setClosing(e.target.value)}
              />
            )}
          </Field>
        ) : null}

        <Button
          variant="primary"
          onClick={onSave}
          disabled={save.isPending || !greeting.trim() || closingInvalid}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
      <PhoneMenuKeys />
    </Card>
  )
}

/** t1401: a recording played as the menu's opening greeting instead of the
 *  spoken text. Uploading or removing saves straight away (it is a file, not a
 *  form field), so it is independent of the card's Save button. */
function RecordedGreeting({
  audio,
  disabled,
}: {
  audio: SupportSettings['ivrGreetingAudio']
  disabled: boolean
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.supportSettings() })

  const upload = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.postFile('/settings/support/ivr-greeting-audio', formData)
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Recording saved')
    },
    onError: (error) =>
      toast.error('Could not upload', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: () => api.delete('/settings/support/ivr-greeting-audio'),
    onSuccess: async () => {
      await refresh()
      toast.success('Recording removed — the written greeting will be spoken')
    },
    onError: (error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!/\.(mp3|wav)$/i.test(file.name) && !/^audio\/(mpeg|mp3|wav|x-wav|wave)$/.test(file.type)) {
      toast.error('Could not upload', 'Please choose an MP3 or WAV recording.')
      return
    }
    if (file.size > MAX_PHONE_AUDIO_BYTES) {
      toast.error(
        'Could not upload',
        `That recording is larger than ${MAX_PHONE_AUDIO_BYTES / 1024 / 1024} MB.`,
      )
      return
    }
    upload.mutate(file)
  }

  return (
    <Field
      label="Recorded greeting"
      hint="Optional. An MP3 or WAV (up to 10 MB) played first instead of the written greeting. The key choices are still read out after it."
    >
      {() => (
        <div className="space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="audio/mpeg,audio/wav,.mp3,.wav"
            className="hidden"
            onChange={onPick}
          />
          {audio ? (
            <>
              <p className="text-sm">
                {audio.filename}{' '}
                <span className="text-foreground-muted">
                  ({(audio.sizeBytes / 1024 / 1024).toFixed(1)} MB)
                </span>
              </p>
              {/* biome-ignore lint/a11y/useMediaCaption: a greeting recording has no captions to offer */}
              <audio
                controls
                preload="metadata"
                className="h-8 w-full"
                src={`${apiBaseUrl}/settings/support/ivr-greeting-audio?v=${encodeURIComponent(audio.uploadedAt)}`}
              />
            </>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || upload.isPending || remove.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="size-3.5" aria-hidden="true" />
              {upload.isPending ? 'Uploading…' : audio ? 'Replace recording' : 'Upload recording'}
            </Button>
            {audio ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={upload.isPending || remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
                Remove
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </Field>
  )
}

/** t1404: the Listen button — plays a key in the voice callers hear, through
 *  the browser softphone. Hidden when browser calling isn't available. */
function useKeyPreview() {
  const toast = useToast()
  const { available, previewing, playIvrKeyPreview, stopIvrKeyPreview } = useSoftphone()
  const toggle = (key: { digit: string; label: string; message: string | null }) => {
    if (previewing) {
      stopIvrKeyPreview()
      return
    }
    playIvrKeyPreview(key, (message) => toast.error('Could not play', message)).catch((error) =>
      toast.error('Could not play', error instanceof Error ? error.message : undefined),
    )
  }
  return { available, previewing, toggle }
}

function PhoneMenuKeys() {
  const { can } = useAuth()
  const preview = useKeyPreview()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('settings:manage')
  const [editing, setEditing] = useState<SupportIvrOption | 'new' | null>(null)

  const { data: options, isLoading } = useQuery({
    queryKey: queryKeys.supportIvrOptions(),
    queryFn: () => api.get<SupportIvrOption[]>('/settings/support/ivr-options'),
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.supportIvrOptions() })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/settings/support/ivr-options/${id}`, { active }),
    onSuccess: invalidate,
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
  })

  const reorder = useMutation({
    mutationFn: ({ id, position }: { id: string; position: number }) =>
      api.patch(`/settings/support/ivr-options/${id}`, { position }),
    onSuccess: invalidate,
    onError: (error) =>
      toast.error('Could not reorder', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/settings/support/ivr-options/${id}`),
    onSuccess: async () => {
      await invalidate()
      toast.success('Removed')
    },
    onError: (error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  const move = (index: number, direction: -1 | 1) => {
    const list = options ?? []
    const other = list[index + direction]
    const current = list[index]
    if (!other || !current) return
    reorder.mutate({ id: current.id, position: other.position })
    reorder.mutate({ id: other.id, position: current.position })
  }

  const nextPosition = (options ?? []).reduce((max, o) => Math.max(max, o.position + 1), 0)

  return (
    <div className="border-t border-border">
      <div className="flex items-center justify-between px-6 py-3">
        <div>
          <p className="text-sm font-medium">Menu keys</p>
          <p className="text-xs text-foreground-muted">
            Read to the caller in this order.
            {preview.available
              ? ' Press the speaker to hear a key in the voice callers hear (plays through this computer).'
              : ''}
          </p>
        </div>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing('new')}>
            <Plus className="size-3.5" aria-hidden="true" /> Add
          </Button>
        ) : null}
      </div>
      {isLoading ? null : !options || options.length === 0 ? (
        <EmptyState
          icon={Hash}
          title="No keys yet"
          description="Add a key, such as 1 for hours and location, before turning the menu on."
        />
      ) : (
        <ul className="divide-y divide-border">
          {options.map((option, index) => (
            <li key={option.id} className="flex items-center gap-2 px-4 py-2.5">
              {canWrite ? (
                <div className="flex flex-col">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    aria-label="Move up"
                    className="text-foreground-subtle hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="size-3" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    disabled={index === options.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label="Move down"
                    className="text-foreground-subtle hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="size-3" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  Press {option.digit} — {option.label}
                </p>
                <p className="truncate text-xs text-foreground-muted">
                  {option.action === 'route' ? 'Continues to call routing' : (option.message ?? '')}
                </p>
              </div>
              {!option.active ? <Badge tone="neutral">Off</Badge> : null}
              {preview.available ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={preview.previewing ? 'Stop playing' : `Listen to key ${option.digit}`}
                  onClick={() =>
                    preview.toggle({
                      digit: option.digit,
                      label: option.label,
                      message: option.action === 'message' ? option.message : null,
                    })
                  }
                >
                  {preview.previewing ? <Square /> : <Volume2 />}
                </Button>
              ) : null}
              {canWrite ? (
                <>
                  <Switch
                    checked={option.active}
                    onCheckedChange={(active) => toggleActive.mutate({ id: option.id, active })}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setEditing(option)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove key ${option.digit}`}
                    onClick={() => remove.mutate(option.id)}
                  >
                    <Trash2 />
                  </Button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <PhoneMenuKeyDialog
        value={editing}
        options={options ?? []}
        nextPosition={nextPosition}
        onClose={() => setEditing(null)}
      />
    </div>
  )
}

function PhoneMenuKeyDialog({
  value,
  options,
  nextPosition,
  onClose,
}: {
  value: SupportIvrOption | 'new' | null
  options: SupportIvrOption[]
  nextPosition: number
  onClose: () => void
}) {
  const toast = useToast()
  const preview = useKeyPreview()
  const queryClient = useQueryClient()
  const [digit, setDigit] = useState('')
  const [label, setLabel] = useState('')
  const [action, setAction] = useState<SupportIvrAction>('message')
  const [message, setMessage] = useState('')

  const editingId = value && value !== 'new' ? value.id : null
  const takenDigits = new Set(
    options.filter((o) => o.active && o.id !== editingId).map((o) => o.digit as string),
  )
  const freeDigits = SUPPORT_IVR_DIGITS.filter((d) => !takenDigits.has(d))

  // Reseed only when the dialog opens on a different key, not on every refetch.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (value === 'new') {
      setDigit(freeDigits[0] ?? '')
      setLabel('')
      setAction('message')
      setMessage('')
    } else if (value) {
      setDigit(value.digit)
      setLabel(value.label)
      setAction(value.action)
      setMessage(value.message ?? '')
    }
  }, [value])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        digit,
        label: label.trim(),
        action,
        message: action === 'message' ? message.trim() : null,
      }
      return value === 'new'
        ? api.post('/settings/support/ivr-options', { ...body, position: nextPosition })
        : api.patch(`/settings/support/ivr-options/${editingId}`, body)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportIvrOptions() })
      toast.success('Saved')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const canSubmit =
    !!digit && !!label.trim() && (action === 'route' || !!message.trim()) && !save.isPending

  return (
    <Dialog open={value !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{value === 'new' ? 'Add menu key' : 'Edit menu key'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Key">
            {(props) => (
              <Select value={digit || undefined} onValueChange={setDigit}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a key" />
                </SelectTrigger>
                <SelectContent>
                  {freeDigits.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field
            label="What the caller hears it offered as"
            hint='Finishes the sentence "For …, press 1." — e.g. "our hours and location".'
          >
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <Field label="When pressed">
            {(props) => (
              <Select value={action} onValueChange={(v) => setAction(v as SupportIvrAction)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="message">Play a message</SelectItem>
                  <SelectItem value="route">Send to customer service</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          {action === 'message' ? (
            <Field
              label="Message"
              hint="Read to the caller, then the menu plays again — e.g. your hours and address."
            >
              {(props) => (
                <Textarea
                  {...props}
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              )}
            </Field>
          ) : null}
        </DialogBody>
        <DialogFooter>
          {preview.available ? (
            <Button
              variant="secondary"
              className="mr-auto"
              disabled={
                !preview.previewing &&
                !(digit && label.trim() && (action === 'route' || message.trim()))
              }
              onClick={() =>
                preview.toggle({
                  digit,
                  label: label.trim(),
                  message: action === 'message' ? message.trim() : null,
                })
              }
            >
              {preview.previewing ? (
                <Square className="size-3.5" aria-hidden="true" />
              ) : (
                <Volume2 className="size-3.5" aria-hidden="true" />
              )}
              {preview.previewing ? 'Stop' : 'Listen'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CallRoutingCard() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingIncoming, setEditingIncoming] = useState<SupportCallTarget | null>(null)

  const { data: targets, isLoading } = useQuery({
    queryKey: queryKeys.supportCallTargets(),
    queryFn: () => api.get<SupportCallTarget[]>('/settings/support/call-targets'),
  })

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.supportCallTargets() })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/settings/support/call-targets/${id}`, { active }),
    onSuccess: invalidate,
    onError: (error) =>
      toast.error('Could not update', error instanceof ApiError ? error.message : undefined),
  })

  const reorder = useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) =>
      api.patch(`/settings/support/call-targets/${id}`, { priority }),
    onSuccess: invalidate,
    onError: (error) =>
      toast.error('Could not reorder', error instanceof ApiError ? error.message : undefined),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/settings/support/call-targets/${id}`),
    onSuccess: async () => {
      await invalidate()
      toast.success('Removed')
    },
    onError: (error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  const move = (index: number, direction: -1 | 1) => {
    const list = targets ?? []
    const other = list[index + direction]
    const current = list[index]
    if (!other || !current) return
    reorder.mutate({ id: current.id, priority: other.priority })
    reorder.mutate({ id: other.id, priority: current.priority })
  }

  return (
    <Card className="max-w-xl">
      <CardHeader className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Phone className="size-4" aria-hidden="true" /> Call routing
          </CardTitle>
          <CardDescription>
            An inbound call rings these in order, one at a time, until someone answers or the list
            runs out — then it goes to voicemail. A phone number has to press 1 to take the call, so
            it never lands in a personal voicemail. Answered calls are recorded. A phone can have a
            separate incoming number (pencil); outgoing calls through it keep using the main number.
          </CardDescription>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setAdding(true)}>
          <Plus className="size-3.5" aria-hidden="true" /> Add
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? null : !targets || targets.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="No one is set to ring"
            description="Every inbound call will go straight to voicemail until someone is added."
          />
        ) : (
          <ul className="divide-y divide-border">
            {targets.map((target, index) => (
              <li key={target.id} className="flex items-center gap-2 px-4 py-2.5">
                <div className="flex flex-col">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    aria-label="Move up"
                    className="text-foreground-subtle hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="size-3" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    disabled={index === targets.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label="Move down"
                    className="text-foreground-subtle hover:text-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="size-3" aria-hidden="true" />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{target.label}</p>
                  <p className="font-identifier text-xs text-foreground-muted">
                    {target.kind === 'client'
                      ? `Browser${target.staffName ? ` · ${target.staffName}` : ''}`
                      : `${target.incomingPhoneNumber ? `Outgoing ${target.phoneNumber} · Incoming ${target.incomingPhoneNumber}` : target.phoneNumber}${target.staffName ? ` · ${target.staffName}` : ''}`}
                  </p>
                </div>
                {!target.active ? <Badge tone="neutral">Off</Badge> : null}
                <Switch
                  checked={target.active}
                  onCheckedChange={(active) => toggleActive.mutate({ id: target.id, active })}
                />
                {target.kind === 'phone' ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Set incoming number for ${target.label}`}
                    onClick={() => setEditingIncoming(target)}
                  >
                    <Pencil />
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${target.label}`}
                  onClick={() => remove.mutate(target.id)}
                >
                  <Trash2 />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AddCallTargetDialog
        open={adding}
        onClose={() => setAdding(false)}
        nextPriority={targets?.length ?? 0}
      />
      <IncomingNumberDialog target={editingIncoming} onClose={() => setEditingIncoming(null)} />
    </Card>
  )
}

/** t1367: set or clear the separate number a linked phone rings on for
 *  incoming calls. Blank = ring the main number; outgoing is never affected. */
function IncomingNumberDialog({
  target,
  onClose,
}: {
  target: SupportCallTarget | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [value, setValue] = useState('')

  // biome-ignore lint/correctness/useExhaustiveDependencies: reseed only when a different row opens
  useEffect(() => {
    setValue(target?.incomingPhoneNumber ?? '')
  }, [target?.id])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/settings/support/call-targets/${target?.id}`, {
        incomingPhoneNumber: value.trim() || null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportCallTargets() })
      toast.success('Saved')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={target !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Incoming number{target ? ` — ${target.label}` : ''}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field
            label="Number for incoming calls"
            hint={`Leave blank to ring ${target?.phoneNumber ?? 'the main number'}. Outgoing calls through this phone always use that number.`}
          >
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AddCallTargetDialog({
  open,
  onClose,
  nextPriority,
}: {
  open: boolean
  onClose: () => void
  nextPriority: number
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<'phone' | 'client'>('phone')
  const [label, setLabel] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [staffId, setStaffId] = useState('')
  const [incomingPhoneNumber, setIncomingPhoneNumber] = useState('')

  const { data: staff } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffOption[]>('/staff'),
    enabled: open,
  })

  const reset = () => {
    setKind('phone')
    setLabel('')
    setPhoneNumber('')
    setStaffId('')
    setIncomingPhoneNumber('')
  }

  const create = useMutation({
    mutationFn: () =>
      api.post('/settings/support/call-targets', {
        kind,
        label,
        phoneNumber: kind === 'phone' ? phoneNumber : null,
        incomingPhoneNumber: kind === 'phone' ? incomingPhoneNumber.trim() || null : null,
        staffId: staffId || null,
        priority: nextPriority,
        active: true,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportCallTargets() })
      toast.success('Added')
      onClose()
      reset()
    },
    onError: (error) =>
      toast.error('Could not add', error instanceof ApiError ? error.message : undefined),
  })

  const activeStaff = (staff ?? []).filter((s) => s.isActive)
  const canSubmit =
    !!label.trim() && (kind === 'phone' ? !!phoneNumber.trim() : !!staffId) && !create.isPending

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add someone to the ring list</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Type">
            {(props) => (
              <Select value={kind} onValueChange={(v) => setKind(v as 'phone' | 'client')}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Phone number</SelectItem>
                  <SelectItem value="client">Staff member's browser</SelectItem>
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Label" hint="How this shows on the list — a name or a role.">
            {(props) => (
              <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          {kind === 'phone' ? (
            <Field label="Phone number">
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          {kind === 'phone' ? (
            <Field
              label="Number for incoming calls (optional)"
              hint="Leave blank to ring the number above. Outgoing calls through this phone always use the number above."
            >
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  value={incomingPhoneNumber}
                  onChange={(e) => setIncomingPhoneNumber(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          <Field
            label="Staff member"
            hint={
              kind === 'client'
                ? 'Whose browser this rings — they must be signed in with the softphone available.'
                : 'Optional — auto-assigns the ticket to them once they answer.'
            }
          >
            {(props) => (
              <Select value={staffId || undefined} onValueChange={setStaffId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {activeStaff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSubmit} onClick={() => create.mutate()}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** t1347: the one-tap notes offered when closing or resolving a ticket. */
function CloseNotesCard() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<string[] | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.supportCloseNotes(),
    queryFn: () => api.get<TicketCloseNotes>('/support-close-notes'),
  })
  useEffect(() => {
    if (data) setDraft(data.notes)
  }, [data])

  const cleaned = (draft ?? []).map((n) => n.trim())
  const hasBlank = cleaned.some((n) => n === '')
  const hasDuplicate = new Set(cleaned.map((n) => n.toLowerCase())).size !== cleaned.length
  const dirty = draft !== null && data !== undefined && cleaned.join('\n') !== data.notes.join('\n')

  const save = useMutation({
    mutationFn: () => api.put<TicketCloseNotes>('/support-close-notes', { notes: cleaned }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportCloseNotes() })
      toast.success('Close notes saved')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const update = (index: number, value: string) =>
    setDraft((d) => (d ?? []).map((n, i) => (i === index ? value : n)))
  const move = (index: number, delta: number) =>
    setDraft((d) => {
      const next = [...(d ?? [])]
      const target = index + delta
      if (target < 0 || target >= next.length) return next
      const [item] = next.splice(index, 1)
      if (item !== undefined) next.splice(target, 0, item)
      return next
    })

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ListChecks className="size-4" aria-hidden="true" /> Ticket close notes
        </CardTitle>
        <CardDescription>
          One-tap notes offered when closing or resolving a ticket. Shared by all staff.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading || draft === null ? null : (
          <>
            <ul className="space-y-2">
              {draft.map((note, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place by position
                <li key={index} className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                      aria-label="Move up"
                      className="text-foreground-subtle hover:text-foreground disabled:opacity-30"
                    >
                      <ArrowUp className="size-3" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      disabled={index === draft.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label="Move down"
                      className="text-foreground-subtle hover:text-foreground disabled:opacity-30"
                    >
                      <ArrowDown className="size-3" aria-hidden="true" />
                    </button>
                  </div>
                  <Input
                    value={note}
                    maxLength={100}
                    aria-label={`Note ${index + 1}`}
                    onChange={(e) => update(index, e.target.value)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${note || 'note'}`}
                    onClick={() => setDraft((d) => (d ?? []).filter((_, i) => i !== index))}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
            {hasBlank ? (
              <p className="text-xs text-danger">Fill in or remove the empty note.</p>
            ) : hasDuplicate ? (
              <p className="text-xs text-danger">Each note can only appear once.</p>
            ) : null}
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="secondary"
                disabled={draft.length >= 30}
                onClick={() => setDraft((d) => [...(d ?? []), ''])}
              >
                <Plus className="size-3.5" aria-hidden="true" /> Add note
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={save.isPending}
                disabled={!dirty || hasBlank || hasDuplicate}
                onClick={() => save.mutate()}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CannedResponsesCard() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('support:write')
  const [editing, setEditing] = useState<SupportCannedResponse | 'new' | null>(null)

  const { data: canned, isLoading } = useQuery({
    queryKey: queryKeys.supportCannedResponses(),
    queryFn: () => api.get<SupportCannedResponse[]>('/support-canned-responses'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/support-canned-responses/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportCannedResponses() })
      toast.success('Removed')
    },
    onError: (error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Card className="max-w-xl">
      <CardHeader className="flex items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <MessagesSquare className="size-4" aria-hidden="true" /> Canned responses
          </CardTitle>
          <CardDescription>
            Ready-made replies offered when texting back from a ticket.
          </CardDescription>
        </div>
        {canWrite ? (
          <Button size="sm" variant="secondary" onClick={() => setEditing('new')}>
            <Plus className="size-3.5" aria-hidden="true" /> Add
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? null : !canned || canned.length === 0 ? (
          <EmptyState
            icon={MessagesSquare}
            title="None yet"
            description="Add a reply staff can reuse."
          />
        ) : (
          <ul className="divide-y divide-border">
            {canned.map((c) => (
              <li key={c.id} className="flex items-start gap-2 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{c.title}</p>
                  <p className="truncate text-xs text-foreground-muted">{c.body}</p>
                </div>
                {canWrite ? (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${c.title}`}
                      onClick={() => remove.mutate(c.id)}
                    >
                      <Trash2 />
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <CannedResponseDialog value={editing} onClose={() => setEditing(null)} />
    </Card>
  )
}

function CannedResponseDialog({
  value,
  onClose,
}: {
  value: SupportCannedResponse | 'new' | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')

  useEffect(() => {
    if (value === 'new') {
      setTitle('')
      setBody('')
    } else if (value) {
      setTitle(value.title)
      setBody(value.body)
    }
  }, [value])

  const save = useMutation({
    mutationFn: () =>
      value === 'new'
        ? api.post('/support-canned-responses', { title, body })
        : api.patch(`/support-canned-responses/${value?.id}`, { title, body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportCannedResponses() })
      toast.success('Saved')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={value !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {value === 'new' ? 'Add canned response' : 'Edit canned response'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Title">
            {(props) => (
              <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} />
            )}
          </Field>
          <Field label="Text">
            {(props) => (
              <Textarea
                {...props}
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!title.trim() || !body.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
